import { typedSupabase } from "../lib/typed-supabase";
import { logger } from "../lib/logger";
import type { IService, ServiceResponse } from "./service-registry";
import { createServiceError} from "./service-registry";
import type { Database } from "@flowstate/types";
// Use proper database types
type UserAchievement = Database["public"]["Tables"]["user_achievements"]["Row"];
type UserCurrentRank = Database["public"]["Views"]["user_current_ranks"]["Row"];
type UserEarnedBadge = Database["public"]["Views"]["user_earned_badges"]["Row"];
// ============================================================================
// ACHIEVEMENT SERVICE - Phase 4 Architecture
// ============================================================================
export interface IAchievementService extends IService {
    awardAchievement(userId: string, achievementType: "rank" | "badge", achievementId: string, name: string, description?: string, emoji?: string, color?: string, rarity?: "common" | "rare" | "epic" | "legendary", level?: number): Promise<ServiceResponse<string>>;
    getUserAchievements(userId?: string): Promise<ServiceResponse<UserAchievement[]>>;
    getUserCurrentRank(userId: string): Promise<ServiceResponse<UserCurrentRank | undefined>>;
    getUserBadges(userId: string, limit?: number): Promise<ServiceResponse<UserEarnedBadge[]>>;
    getAchievementStats(): Promise<ServiceResponse<{
        totalAchievements: number;
        totalRanks: number;
        totalBadges: number;
        totalUsers: number;
        rarityDistribution: Record<string, number>;
    }>>;
    hasAchievement(userId: string, achievementType: "rank" | "badge", achievementId: string): Promise<ServiceResponse<boolean>>;
}
export class AchievementService implements IAchievementService {
    readonly name = "AchievementService";
    async initialize(): Promise<void> {
        logger.info("✅ AchievementService initialized");
    }
    async dispose(): Promise<void> {
        logger.info("🧹 AchievementService disposed");
    }
    /**
     * Award an achievement to a user
     */
    async awardAchievement(userId: string, achievementType: "rank" | "badge", achievementId: string, name: string, description?: string, emoji?: string, color?: string, rarity?: "common" | "rare" | "epic" | "legendary", level?: number): Promise<ServiceResponse<string>> {
        const startTime = Date.now();
        try {
            const { data, error } = await typedSupabase.rpc("award_achievement", {
                p_user_id: userId,
                p_achievement_type: achievementType,
                p_achievement_id: achievementId,
                p_name: name,
                p_description: description,
                p_emoji: emoji,
                p_color: color,
                p_rarity: rarity,
                p_level: level,
            });
            if (error) {
                return { success: false, error: error.message, code: "AWARD_FAILED", service: this.name };
            }
            return { success: true, data: data as string };
        }
        catch (error) {
            const serviceError = error instanceof Error ? error : new Error("Unknown error");
            return { success: false, error: serviceError.message, code: "AWARD_FAILED", service: this.name };
        }
    }
    /**
     * Get all achievements for a user
     */
    async getUserAchievements(userId?: string): Promise<ServiceResponse<UserAchievement[]>> {
        const startTime = Date.now();
        try {
            const { data, error } = await typedSupabase.rpc("get_user_achievements", {
                p_user_id: userId,
            });
            if (error) {
                return { success: false, error: error.message, code: "FETCH_FAILED", service: this.name };
            }
            return { success: true, data: (data || []) as UserAchievement[] };
        }
        catch (error) {
            const serviceError = error instanceof Error ? error : new Error("Unknown error");
            return { success: false, error: serviceError.message, code: "FETCH_FAILED", service: this.name };
        }
    }
    /**
     * Get current rank for a user
     */
    async getUserCurrentRank(userId: string): Promise<ServiceResponse<UserCurrentRank | undefined>> {
        const startTime = Date.now();
        try {
            const { data, error } = await typedSupabase
                .from("user_current_ranks")
                .select("*")
                .eq("user_id", userId)
                .single();
            // Handle "No rows found" as a valid undefined result
            if (error && error.code === "PGRST116") {
                return { success: true, data: undefined };
            }
            if (error) {
                return { success: false, error: error.message, code: "FETCH_FAILED", service: this.name };
            }
            return { success: true, data: data as UserCurrentRank };
        }
        catch (error) {
            const serviceError = error instanceof Error ? error : new Error("Unknown error");
            return { success: false, error: serviceError.message, code: "FETCH_FAILED", service: this.name };
        }
    }
    /**
     * Get all badges for a user
     */
    async getUserBadges(userId: string, limit?: number): Promise<ServiceResponse<UserEarnedBadge[]>> {
        const startTime = Date.now();
        try {
            let query = typedSupabase
                .from("user_earned_badges")
                .select("*")
                .eq("user_id", userId)
                .order("achieved_at", { ascending: false });
            if (limit) {
                query = query.limit(limit);
            }
            const { data, error } = await query;
            if (error) {
                return { success: false, error: error.message, code: "FETCH_FAILED", service: this.name };
            }
            return { success: true, data: Array.isArray(data) ? data as UserEarnedBadge[] : [] };
        }
        catch (error) {
            const serviceError = error instanceof Error ? error : new Error("Unknown error");
            return { success: false, error: serviceError.message, code: "FETCH_FAILED", service: this.name };
        }
    }
    /**
     * Get achievement statistics
     */
    async getAchievementStats(): Promise<ServiceResponse<{
        totalAchievements: number;
        totalRanks: number;
        totalBadges: number;
        totalUsers: number;
        rarityDistribution: Record<string, number>;
    }>> {
        const startTime = Date.now();
        try {
            const { data: achievements, error: achievementsError } = await typedSupabase
                .from("user_achievements")
                .select("achievement_type, rarity, user_id");
            if (achievementsError) {
                return { success: false, error: achievementsError.message, code: "FETCH_FAILED", service: this.name };
            }
            const safeAchievements = Array.isArray(achievements) ? achievements : [];
            const totalAchievements = safeAchievements.length;
            const totalRanks = safeAchievements.filter((a) => a.achievement_type === "rank").length;
            const totalBadges = safeAchievements.filter((a) => a.achievement_type === "badge").length;
            const totalUsers = new Set(safeAchievements.map((a) => a.user_id)).size;
            const rarityDistribution: Record<string, number> = {};
            safeAchievements.forEach((achievement) => {
                const rarity = String(achievement.rarity || "common");
                rarityDistribution[rarity] = (rarityDistribution[rarity] || 0) + 1;
            });
            return { success: true, data: {
                    totalAchievements,
                    totalRanks,
                    totalBadges,
                    totalUsers,
                    rarityDistribution,
                } };
        }
        catch (error) {
            const serviceError = error instanceof Error ? error : new Error("Unknown error");
            return { success: false, error: serviceError.message, code: "FETCH_FAILED", service: this.name };
        }
    }
    /**
     * Check if user has specific achievement
     */
    async hasAchievement(userId: string, achievementType: "rank" | "badge", achievementId: string): Promise<ServiceResponse<boolean>> {
        const startTime = Date.now();
        try {
            const { data, error } = await typedSupabase
                .from("user_achievements")
                .select("id")
                .eq("user_id", userId)
                .eq("achievement_type", achievementType)
                .eq("achievement_id", achievementId)
                .single();
            // Handle "No rows found" as false (doesn't have achievement)
            if (error && error.code === "PGRST116") {
                return { success: true, data: false };
            }
            if (error) {
                return { success: false, error: error.message, code: "CHECK_FAILED", service: this.name };
            }
            return { success: true, data: !!data };
        }
        catch (error) {
            const serviceError = error instanceof Error ? error : new Error("Unknown error");
            return { success: false, error: serviceError.message, code: "CHECK_FAILED", service: this.name };
        }
    }
}
// ============================================================================
// SINGLETON EXPORT - Following architectural consolidation plan
// ============================================================================
export const achievementService = new AchievementService();
